import { useState } from "react";
import { api, errMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { MoreHorizontal, Eye, Pencil, Pause, XCircle, RotateCcw, Archive, Trash2, EyeOff, Shield, FlaskConical } from "lucide-react";
import { toast } from "sonner";

/**
 * Reusable Phase 17 administrative actions menu for credential / license /
 * school / school_application / application rows. Server-enforced authority
 * (require_role("admin")) — this dropdown only renders when the consumer
 * already gates on isAdmin.
 *
 * Props:
 *   collection: "credentials" | "licenses" | "schools" | "school_applications" | "applications"
 *   record:     the row (must have an `id`)
 *   onView:     optional callback to open a read-only view
 *   onEdit:     optional callback to open the edit drawer/modal
 *   onChange:   parent re-fetch hook
 */
export function AdminActionsMenu({ collection, record, onView, onEdit, onChange }) {
  const [confirm, setConfirm] = useState(null); // { title, body, action, danger }
  const [purgeStep, setPurgeStep] = useState(0); // 0 idle, 1 first confirm, 2 second confirm

  const run = async (label, fn) => {
    try {
      await fn();
      toast.success(label);
      onChange?.();
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setConfirm(null);
      setPurgeStep(0);
    }
  };

  const lifecycle = (action, label) => api.post(`/admin/records/${collection}/${record.id}/${action}`, {});
  const softDelete = () => api.delete(`/admin/records/${collection}/${record.id}`);
  const purge = () => api.post(`/admin/records/${collection}/${record.id}/purge`, {}, { headers: { "X-Confirm-Purge": "yes" } });
  const toggleVisibility = (v) => api.patch(`/admin/records/${collection}/${record.id}/visibility`, { visible: v });
  const toggleSample = (v) => api.patch(`/admin/records/${collection}/${record.id}/sample`, { is_sample: v });

  const supportsVisibility = collection === "credentials" || collection === "schools";

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`actions-${collection}-${record.id}`}>
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>Actions</DropdownMenuLabel>
          {onView && (
            <DropdownMenuItem onClick={() => onView(record)} data-testid={`action-view-${record.id}`}>
              <Eye className="h-4 w-4 mr-2" /> View
            </DropdownMenuItem>
          )}
          {onEdit && (
            <DropdownMenuItem onClick={() => onEdit(record)} data-testid={`action-edit-${record.id}`}>
              <Pencil className="h-4 w-4 mr-2" /> Edit
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => setConfirm({
              title: "Suspend record",
              body: "This will temporarily mark the record as suspended. The record will be hidden from the public registry.",
              action: () => run("Suspended", () => lifecycle("suspend")),
            })}
            data-testid={`action-suspend-${record.id}`}
          >
            <Pause className="h-4 w-4 mr-2" /> Suspend
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => setConfirm({
              title: "Revoke record",
              body: "Revocation permanently terminates this record. It will remain in the internal system as a historical entry.",
              action: () => run("Revoked", () => lifecycle("revoke")),
              danger: true,
            })}
            data-testid={`action-revoke-${record.id}`}
          >
            <XCircle className="h-4 w-4 mr-2" /> Revoke
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => run("Reinstated", () => lifecycle("reinstate"))}
            data-testid={`action-reinstate-${record.id}`}
          >
            <RotateCcw className="h-4 w-4 mr-2" /> Reinstate
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          {supportsVisibility && (
            <DropdownMenuItem
              onClick={() => run(
                record.visible_in_registry === false ? "Made public" : "Hidden from registry",
                () => toggleVisibility(record.visible_in_registry === false)
              )}
              data-testid={`action-visibility-${record.id}`}
            >
              {record.visible_in_registry === false
                ? <><Shield className="h-4 w-4 mr-2" /> Show in registry</>
                : <><EyeOff className="h-4 w-4 mr-2" /> Hide from registry</>}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            onClick={() => run(
              record.is_sample ? "Marked as live" : "Marked as sample",
              () => toggleSample(!record.is_sample)
            )}
            data-testid={`action-sample-${record.id}`}
          >
            <FlaskConical className="h-4 w-4 mr-2" /> {record.is_sample ? "Convert to live" : "Convert to sample"}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => run(
              record.is_archived ? "Unarchived" : "Archived",
              () => lifecycle(record.is_archived ? "unarchive" : "archive")
            )}
            data-testid={`action-archive-${record.id}`}
          >
            <Archive className="h-4 w-4 mr-2" /> {record.is_archived ? "Unarchive" : "Archive"}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="text-red-700 focus:text-red-700"
            onClick={() => setConfirm({
              title: "Delete record",
              body: "This soft-deletes the record (hidden from all views, retained internally for audit recovery). You can restore it later or permanently purge it.",
              action: () => run("Deleted", softDelete),
              danger: true,
            })}
            data-testid={`action-delete-${record.id}`}
          >
            <Trash2 className="h-4 w-4 mr-2" /> Delete
          </DropdownMenuItem>
          <DropdownMenuItem
            className="text-red-700 focus:text-red-700"
            onClick={() => setPurgeStep(1)}
            data-testid={`action-purge-${record.id}`}
          >
            <Trash2 className="h-4 w-4 mr-2" /> Purge permanently…
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm?.title}</AlertDialogTitle>
            <AlertDialogDescription>{confirm?.body}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirm?.action}
              className={confirm?.danger ? "bg-red-600 text-white hover:bg-red-700" : ""}
              data-testid="confirm-ok"
            >
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={purgeStep > 0} onOpenChange={(o) => !o && setPurgeStep(0)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {purgeStep === 1 ? "Permanently purge this record?" : "Are you absolutely sure?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {purgeStep === 1
                ? "Purging will remove this record from the database irrecoverably. An audit-log snapshot is retained for forensic recovery, but the live record will be gone."
                : "This is your second and final confirmation. Press Confirm to permanently delete the record."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPurgeStep(0)} data-testid="purge-cancel">Cancel</AlertDialogCancel>
            {purgeStep === 1 ? (
              <AlertDialogAction
                onClick={() => setPurgeStep(2)}
                className="bg-red-600 text-white hover:bg-red-700"
                data-testid="purge-step-1"
              >
                Continue
              </AlertDialogAction>
            ) : (
              <AlertDialogAction
                onClick={() => run("Purged", purge)}
                className="bg-red-600 text-white hover:bg-red-700"
                data-testid="purge-confirm"
              >
                Purge permanently
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
